////////////////////////////////////////////
// FORMAT HELPERS — SOL/ETH/USD DISPLAY + SHARED SMALL UTILITIES
////////////////////////////////////////////
//
// PORT OF web/app/util.js (MINUS HTML ESCAPING — REACT ESCAPES BY DEFAULT).
import { formatEther } from "viem";
import { SOL, WEI } from "@/config/protocol";

// SOL DISPLAY
export const sol = (v: number | bigint) => Number(v) / SOL;
export const fmtSol = (v: number | bigint) =>
  sol(v).toLocaleString(undefined, { maximumFractionDigits: 4 });

// USD DISPLAY — null PRICE -> null (CALLERS FALL BACK TO NATIVE-UNIT DISPLAY)
export function usd(lamports: number | bigint, solUsd: number | null): string | null {

  if (solUsd == null) return null;

  const v = sol(lamports) * solUsd;
  return v.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: v >= 100 ? 0 : 2 });
}

// ETH DISPLAY — SAFE FOR DISPLAY ONLY; NEVER DERIVE A CONTRACT-CALL WEI
// AMOUNT FROM THESE (USE viem parseEther ON THE RAW INPUT INSTEAD)
export const eth = (v: number | bigint) => Number(v) / WEI;
export const fmtEth = (v: number | bigint) =>
  eth(v).toLocaleString(undefined, { maximumFractionDigits: 5 });

export function usdEth(wei: number | bigint, ethUsd: number | null): string | null {

  if (ethUsd == null) return null;

  const v = eth(wei) * ethUsd;
  return v.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: v >= 100 ? 0 : 2 });
}

// RH TOKEN DISPLAY — FuzeToken IS A VANILLA ERC20, 18 DECIMALS (OZ DEFAULT,
// NO decimals() OVERRIDE — SEE contracts/src/FuzeCurveMath.sol's OWN NOTE).
// NOT fmtTokens6 (position-math.ts) — THAT'S SPL'S 6-DECIMAL CONVENTION,
// WOULD BE OFF BY 1e12 HERE. SAME formatEther-BASED SHAPE AS SwapBox.tsx's
// OWN LOCAL fmtTok (kept local there; promoted here since BoughtCard needs
// the identical rule for a surface SwapBox doesn't touch).
export function fmtTokensEth(baseUnits: string | number | bigint): string {

  const n = Number(formatEther(BigInt(baseUnits)));
  return n >= 1e6
    ? n.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// HOLDING % — NEVER READS AS "0.0%" FOR A GENUINELY NONZERO HOLDING. A real
// but tiny dev-buy (raw > 0) rounded to 1 decimal can display as "0.0%",
// directly contradicting the disclosure sentence right next to it ("bought
// in for X SOL ... holding 0.0%") — found live on a real launch (a 0.003 SOL
// buy against a 1B-token supply is ~0.011%, which .toFixed(1) rounds to
// "0.0"). Shows "<0.01%" instead once a nonzero raw amount rounds to zero at
// 2 decimals, so the copy never implies a real buyer holds nothing.
export function fmtHoldingPct(raw: bigint, totalSupply: bigint): string {

  if (raw <= 0n) return "0%";
  const pct = Number(raw * 1_000_000n / totalSupply) / 10_000; // 2-decimal-safe fixed-point math
  return pct < 0.01 ? "<0.01%" : pct.toFixed(2) + "%";
}

// SHORT ADDRESS — 4…4 (SOLANA) / 6…4 (EVM)
export const shortAddr = (k: string | null | undefined) =>
  k ? k.slice(0, 4) + "…" + k.slice(-4) : null;
export const shortAddrEvm = (a: string | null | undefined) =>
  a ? a.slice(0, 6) + "…" + a.slice(-4) : null;

// UTF-8 BYTE LENGTH — NAME/SYMBOL/URI CAP CHECKS
export const byteLen = (s: string) => new TextEncoder().encode(s).length;

// SUBSCRIPT-ZERO SMALL NUMBERS — "$2.7e-8" READS LIKE A MATH PAPER; THE
// MEMECOIN-STANDARD "0.0₇27" (ZEROS COUNT AS A SUBSCRIPT, THEN SIG DIGITS)
// IS COMPACT ENOUGH FOR STAT CELLS AND DISCOVER-CARD TILES ALIKE
const SUB = "₀₁₂₃₄₅₆₇₈₉";
export function fmtSubZero(v: number): string {

  const zeros = Math.ceil(-Math.log10(v)) - 1; // 0s BETWEEN "." AND THE FIRST SIG DIGIT
  const mant = String(Math.round(v * 10 ** (zeros + 3))).replace(/0+$/, "") || "0";
  return "0.0" + String(zeros).split("").map((d) => SUB[+d]).join("") + mant;
}

// SIG-FIG PRICE — SUB-CENT TOKEN PRICES (E.G. 1.1e-11 ETH) KEEP MEANINGFUL
// DIGITS; toFixed WOULD PRINT ZEROS, toPrecision PRINTS SCI-NOTATION
export function fmtPrice(v: number | null | undefined): string {

  if (v == null) return "—";
  if (v === 0) return "0";
  if (v > 0 && v < 0.001) return fmtSubZero(v);
  return Number(v).toPrecision(3);
}

// NATIVE VALUE -> USD STRING — "" WHEN NO PRICE FEED (CALLERS FALL BACK TO
// NATIVE-UNIT DISPLAY). NAMED GENERICALLY (NOT usdEth) — THE CALLER PASSES
// WHICHEVER NATIVE AMOUNT IT HAS (ETH ON RH, SOL ON SOLANA) + THAT CHAIN'S
// OWN LIVE USD PRICE (usePrices), SAME AS market/MarketCard.tsx's ORIGINAL.
export function fmtUsdCompact(nativeVal: number | null | undefined, nativeUsd: number | null): string {

  if (nativeVal == null) return "";
  if (nativeUsd == null) return "";
  const usdVal = nativeVal * nativeUsd;
  if (usdVal === 0) return "$0";
  if (usdVal > 0 && usdVal < 0.01) return "$" + fmtSubZero(usdVal);
  if (Math.abs(usdVal) < 0.01) return "$" + usdVal.toPrecision(2);
  return usdVal.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: usdVal >= 100 ? 0 : 2 });
}

// COMPACT AGE — "12s" / "44m" / "3h" / "12d" / "6mo" / "2y" (CARD FOOTERS)
export function fmtAgo(secs: number): string {

  const s = Math.max(0, Math.floor(secs));
  if (s < 60)
    return s + "s";
  const m = Math.floor(s / 60);
  if (m < 60)
    return m + "m";
  const h = Math.floor(m / 60);
  if (h < 24)
    return h + "h";
  const d = Math.floor(h / 24);
  if (d < 30)
    return d + "d";
  const mo = Math.floor(d / 30);
  if (mo < 12)
    return mo + "mo";
  return Math.floor(mo / 12) + "y";
}

// SAFE MEDIA — CREATOR-CONTROLLED IMAGE/BANNER URLS REACH <img src> /
// background-image SINKS; ACCEPT ONLY http(s) WITH NO CONTEXT-BREAKOUT CHARS
export function safeMedia(u: unknown): string | null {

  return typeof u === "string" && /^https?:\/\//i.test(u) && !/["'()\\<>\s]/.test(u) ? u : null;
}

// SHARE INTENTS — X + TELEGRAM TAKE TEXT AND URL SEPARATELY
export const xIntent = (text: string, url: string) =>
  "https://twitter.com/intent/tweet?text=" + encodeURIComponent(text) + "&url=" + encodeURIComponent(url);
export const tgIntent = (text: string, url: string) =>
  "https://t.me/share/url?url=" + encodeURIComponent(url) + "&text=" + encodeURIComponent(text);
